import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

const FUNCTIONS_REGION =
  (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined) ?? "us-central1";

type LogPayload = {
  message: string;
  stack?: string;
  route: string;
  releaseId?: string;
  fingerprint: string;
  context?: Record<string, unknown>;
};

let logFn: ((payload: LogPayload) => Promise<unknown>) | null = null;

function getLogFn(): (payload: LogPayload) => Promise<unknown> {
  if (logFn) return logFn;
  const callable = httpsCallable<LogPayload, { ok: true }>(
    getFunctions(getApp(), FUNCTIONS_REGION),
    "logClientError",
  );
  logFn = (payload) => callable(payload);
  return logFn;
}

function fingerprint(message: string, stack?: string): string {
  const firstStackLines = (stack ?? "")
    .split("\n")
    .slice(1, 4)
    .map((line) => line.trim())
    .join("|");
  const raw = `${message}::${firstStackLines}`;
  try {
    return btoa(unescape(encodeURIComponent(raw))).slice(0, 60);
  } catch {
    return raw.slice(0, 60);
  }
}

/**
 * Dedupe por fingerprint (BUG-37).
 *
 * Um erro em loop de render ou uma promise rejeitada em cascata disparava o
 * mesmo evento várias vezes em segundos (o caso real: 5 "Connection to
 * IndexedDatabase server lost" em ~3s). Isso inflava custo do
 * `logClientError` e afogava o alerta. Primeira ocorrência da janela passa;
 * repetições idênticas são descartadas.
 */
const DEDUPE_WINDOW_MS = 60_000;
const recentFingerprints = new Map<string, number>();

function isDuplicate(fp: string): boolean {
  const now = Date.now();
  for (const [key, seenAt] of recentFingerprints) {
    if (now - seenAt > DEDUPE_WINDOW_MS) recentFingerprints.delete(key);
  }
  const last = recentFingerprints.get(fp);
  recentFingerprints.set(fp, now);
  return last !== undefined && now - last < DEDUPE_WINDOW_MS;
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const payload: LogPayload = {
    message: err.message || "unknown_error",
    stack: err.stack,
    route: typeof window !== "undefined" ? window.location.pathname : "unknown",
    releaseId: import.meta.env.VITE_APP_VERSION as string | undefined,
    fingerprint: fingerprint(err.message, err.stack),
    context,
  };

  if (import.meta.env.DEV) {
    console.error("[Lura] captureException:", error, context);
    // BUG-37: erro de localhost NÃO vai pro Cloud Logging de produção — era o
    // que fazia o alerta "Erros nas Functions" disparar com a sessão de dev do
    // fundador. Pra exercitar o caminho de envio localmente, setar
    // VITE_LOG_CLIENT_ERRORS=true.
    if (import.meta.env.VITE_LOG_CLIENT_ERRORS !== "true") return;
  }

  if (isDuplicate(payload.fingerprint)) return;

  try {
    void getLogFn()(payload).catch((sendErr) => {
      if (import.meta.env.DEV) {
        console.error("[Lura] logClientError failed", sendErr, payload);
      }
    });
  } catch (sendErr) {
    if (import.meta.env.DEV) {
      console.error("[Lura] logClientError unavailable", sendErr);
    }
  }
}

const ENVIRONMENT_DEGRADATION_RE =
  /connection to indexeddatabase server lost|indexeddb|quotaexceeded|quota_exceeded/i;

function isEnvironmentDegradation(reason: unknown): boolean {
  if (!reason) return false;
  const message = reason instanceof Error ? reason.message : String(reason);
  return ENVIRONMENT_DEGRADATION_RE.test(message);
}

export function initMonitoring(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    captureException(event.error ?? event.message, { kind: "window.onerror" });
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureException(event.reason, {
      kind: "unhandledrejection",
      // Marca degradações conhecidas do ambiente do browser (a persistência
      // IndexedDB do Firestore cai em multi-aba, storage evictado ou devtools
      // limpando dados). O SDK se recupera sozinho, então isso é sinal de
      // ambiente, não bug do app — o campo existe pra dar pra filtrar no alerta.
      ...(isEnvironmentDegradation(event.reason) ? { degraded: true } : {}),
    });
  });
}
